import { Dispatch, forwardRef, JSX, SetStateAction, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import './style.scss';
import RequiredAsterisk from '../RequiredAsterisk';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import LogService from '../../../services/log-service/log.service';
import { TranslationService } from '../../../services/translation.service';

export interface ITextFieldFormGroupState {
  value: string;
  validationInfo: ValidationInfo;
}

interface ITextFieldFormGroupProps {
  inputId: string;
  serverValidationName?: string;
  hideLabel?: boolean;
  labelText: string;
  placeholder?: string;
  inputMode?: 'text' | 'search' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
  disabled?: boolean;
  pattern?: string;
  required?: boolean;
  hideRequiredAsterisk?: boolean;
  autocomplete?: string;
  type?: string;
  renderAsTextArea?: boolean;
  autoAdjustTextAreaHeight?: boolean;
  autoAdjustMaxRows?: number;
  ariaDescribedBy?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
  helpTextElement?: JSX.Element;
  disableLogging?: boolean;
  state: ITextFieldFormGroupState | [ITextFieldFormGroupState, Dispatch<SetStateAction<ITextFieldFormGroupState>>];
  onChange?: (value: string) => void;
  onKeyUp?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

export default forwardRef<IValidatable, ITextFieldFormGroupProps>(function TextFieldFormGroup(
  {
    inputId,
    serverValidationName = undefined,
    labelText,
    hideLabel = false,
    placeholder = undefined,
    inputMode = undefined,
    disabled = false,
    pattern = undefined,
    required = false,
    hideRequiredAsterisk = false,
    autocomplete = undefined,
    type = 'text',
    renderAsTextArea = false,
    autoAdjustTextAreaHeight = false,
    autoAdjustMaxRows = undefined,
    ariaDescribedBy = undefined,
    className = undefined,
    rows = undefined,
    maxLength = undefined,
    helpTextElement = undefined,
    disableLogging = true,
    state,
    onChange = undefined,
    onKeyUp = undefined,
    onKeyDown = undefined,
  }: ITextFieldFormGroupProps,
  ref,
) {
  // === VARIABLES ===
  const errorTextId = `${inputId}-error`;
  const helpTextId = helpTextElement ? `${inputId}-help` : undefined;
  const characterCounterId = maxLength && renderAsTextArea ? `${inputId}-character-counter` : undefined;
  const stateDerived = Array.isArray(state) ? state[0] : state;

  // === REFS ===
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const characterCounterAriaLiveRef = useRef<HTMLDivElement>(null);

  // === MEMOIZED VALUES ===
  const textareaStyle = useMemo(() => {
    return autoAdjustTextAreaHeight ? { resize: 'none' as const } : {};
  }, [autoAdjustTextAreaHeight]);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).requiredIf(required, stateDerived.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
      );
    },
  }));

  useEffect(() => {
    function adjustTextareaHeight() {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        const newHeight = textarea.scrollHeight + 2;
        if (autoAdjustMaxRows) {
          const maxHeightPx = 28 + autoAdjustMaxRows * 22; // Empirically determined, works best for text size 100%
          if (newHeight > maxHeightPx) {
            textarea.style.height = `${maxHeightPx}px`;
            textarea.style.overflowY = 'auto';
            return;
          }
        }
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    }
    if (autoAdjustTextAreaHeight && renderAsTextArea) {
      adjustTextareaHeight();
    }
  }, [stateDerived.value, renderAsTextArea, autoAdjustTextAreaHeight, autoAdjustMaxRows]);

  useEffect(() => {
    if (!maxLength || !renderAsTextArea || !characterCounterAriaLiveRef.current) return;

    const timeoutId = setTimeout(() => {
      if (characterCounterAriaLiveRef.current) {
        const charactersLeft = maxLength - stateDerived.value.length;
        characterCounterAriaLiveRef.current.textContent = TranslationService.get('common.validation.maxcharacterscharactersleft').replace('{0}', String(charactersLeft));
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [stateDerived.value, maxLength, renderAsTextArea]);

  // === HELPER FUNCTIONS ===
  function createAriaDescribedBy(): string | undefined {
    if (!stateDerived.validationInfo.isInvalid && !ariaDescribedBy && !helpTextElement && !characterCounterId) return undefined;
    return `${stateDerived.validationInfo.isInvalid ? errorTextId : ''} ${ariaDescribedBy ?? ''} ${helpTextId ?? ''} ${characterCounterId ?? ''}`.trim().replace(/\s+/g, ' ');
  }

  return (
    <div className={`text-field-form-group ${className ?? ''} ${disableLogging ? 'disable-logging' : ''}`}>
      <label htmlFor={inputId} className={`form-label ${hideLabel ? 'visually-hidden' : ''}`.trimEnd()}>
        {labelText}
        {required && !hideRequiredAsterisk && <RequiredAsterisk />}
      </label>
      {!renderAsTextArea ? (
        <input
          id={inputId}
          value={stateDerived.value}
          type={type}
          inputMode={inputMode}
          disabled={disabled}
          pattern={pattern}
          className={`form-control ${stateDerived.validationInfo.isInvalid ? 'is-invalid' : ''} ${disableLogging ? LogService.disableLoggingCssClassName : ''}`}
          placeholder={placeholder}
          aria-invalid={stateDerived.validationInfo.isInvalid ? 'true' : 'false'}
          aria-describedby={createAriaDescribedBy()}
          aria-required={required}
          autoComplete={autocomplete}
          maxLength={maxLength}
          onChange={(e) => (Array.isArray(state) ? state[1]({ ...state[0], value: e.target.value }) : onChange?.(e.target.value))}
          onKeyUp={() => (Array.isArray(state) ? state[1]({ ...state[0], validationInfo: new ValidationInfo() }) : onKeyUp?.())}
          onKeyDown={(e) => onKeyDown?.(e)}
        />
      ) : (
        <>
          <textarea
            id={inputId}
            ref={textareaRef}
            value={stateDerived.value}
            disabled={disabled}
            className={`form-control ${stateDerived.validationInfo.isInvalid ? 'is-invalid' : ''} ${disableLogging ? LogService.disableLoggingCssClassName : ''}`}
            placeholder={placeholder}
            aria-invalid={stateDerived.validationInfo.isInvalid ? 'true' : 'false'}
            aria-describedby={createAriaDescribedBy()}
            aria-required={required}
            autoComplete={autocomplete}
            rows={rows}
            maxLength={maxLength}
            style={textareaStyle}
            onChange={(e) => (Array.isArray(state) ? state[1]({ ...state[0], value: e.target.value }) : onChange?.(e.target.value))}
            onKeyUp={() => (Array.isArray(state) ? state[1]({ ...state[0], validationInfo: new ValidationInfo() }) : onKeyUp?.())}
            onKeyDown={(e) => onKeyDown?.(e)}
          />
          {maxLength && (
            <>
              <div className="text-muted mt-1" aria-hidden="true">
                {TranslationService.get('common.validation.maxcharacterscharactersleft').replace('{0}', String(maxLength - stateDerived.value.length))}
              </div>
              <div ref={characterCounterAriaLiveRef} id={characterCounterId} className="visually-hidden" aria-live="polite"></div>
            </>
          )}
        </>
      )}
      {stateDerived.validationInfo.isInvalid && (
        <div id={errorTextId} className="invalid-feedback">
          {stateDerived.validationInfo.getErrors().map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
      {helpTextElement && (
        <div className="form-text">
          <div id={helpTextId}>{helpTextElement}</div>
        </div>
      )}
    </div>
  );
});
